import React from 'react'
import {fmN, fmP} from '../../helpers/format'
import useContainerWidth from '../../hooks/useContainerWidth'
import {tipHandlers, useTooltip} from './Tooltip'

const MONO = 'SFMono-Regular,Consolas,monospace'

// Số mục đầu tiên đạt 80% tích luỹ, tính trên tổng total. API chỉ trả các mục đầu (top N),
// nên có thể chưa đạt 80%: khi đó reached = false và share là tỉ trọng cộng dồn của các mục có
export const paretoK80 = (items, total) => {
    let cumulative = 0
    const index = items.findIndex(item => {
        cumulative += total ? item.value / total : 0

        return cumulative >= 0.8
    })

    return index === -1
        ? {count: items.length, reached: false, share: cumulative}
        : {count: index + 1, reached: true, share: cumulative}
}

/**
 * Pareto port từ pareto() của bản mẫu: cột = % đóng góp, đường = % tích luỹ, cùng một thang.
 * items: [{label, value}] đã sắp giảm dần; total: tổng để tính %, mặc định là tổng items
 */
const Pareto = ({items, total, max = 12, height = 330}) => {
    const [ref, width] = useContainerWidth()
    const tooltip = useTooltip()

    if (!items.length) return <div ref={ref}><div className="empty">Không có dữ liệu.</div></div>

    const tot = total || items.reduce((sum, item) => sum + item.value, 0)
    const top = items.slice(0, max)
    const W = width || 340
    const H = height
    const L = 50, R = 42, T = 18, B = 86
    const iw = W - L - R, ih = H - T - B, n = top.length, slot = iw / n, bw = Math.min(slot * 0.62, 54)
    const Y = (v) => T + ih - v * ih
    // Kẹp ở 100% để số lệch giữa hai nguồn không vẽ tràn khung
    const share = (value) => Math.min(1, value / tot)
    let cumulative = 0
    const cums = top.map(item => {
        cumulative = Math.min(1, cumulative + share(item.value))

        return cumulative
    })
    const points = top.map((item, p) => [L + p * slot + slot / 2, Y(cums[p])])

    const tip = (i) => tipHandlers(tooltip, (
        <>
            <div><b>{top[i].label}</b></div>
            <div><span className="k">Số lỗi</span> <b>{fmN(top[i].value)}</b></div>
            <div><span className="k">Đóng góp</span> <b>{fmP(top[i].value / tot)}</b></div>
            <div><span className="k">Tích luỹ</span> <b>{fmP(cums[i])}</b></div>
        </>
    ))
    // Trục ngang chật chỗ (đến 14 cột), nhãn dài phải rút gọn; "…" báo đã cắt, xem đủ tên qua tooltip (rê vào cột hoặc nhãn)
    const axisLabel = (label) => String(label).length > 16 ? `${String(label).slice(0, 16)}…` : String(label)

    return (
        <div ref={ref}>
            <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Pareto">
                {[0, 1, 2, 3, 4].map(i => (
                    <g key={`grid-${i}`}>
                        <line x1={L} x2={L + iw} y1={Y(i / 4)} y2={Y(i / 4)} stroke="var(--grid)"/>
                        <text x={L - 6} y={Y(i / 4) + 4} textAnchor="end" fontSize="10" fontFamily={MONO} fill="var(--ink-3)">{i * 25}%</text>
                    </g>
                ))}
                <line x1={L} x2={L + iw} y1={Y(0.8)} y2={Y(0.8)} stroke="var(--s8)" strokeDasharray="4 3" strokeWidth="1.5"/>
                <text x={L + iw + 4} y={Y(0.8) + 4} fontSize="10" fill="var(--s8)">80%</text>
                {top.map((item, p) => {
                    const cx = L + p * slot + slot / 2
                    const v = share(item.value)

                    return (
                        <g key={`bar-${p}`}>
                            <rect x={cx - bw / 2} y={Y(v)} width={bw} height={v * ih} rx="1.5" fill="var(--s1)" style={{cursor: 'pointer'}} {...tip(p)}/>
                            <text x={cx} y={Y(v) - 5} textAnchor="middle" fontSize="10" fontFamily={MONO} fill="var(--ink-2)">{fmN(item.value)}</text>
                            <text x={cx} y={T + ih + 12} textAnchor="end" fontSize="10" fill="var(--ink-3)" transform={`rotate(-40 ${cx} ${T + ih + 12})`} style={{cursor: 'pointer'}} {...tip(p)}>{axisLabel(item.label)}</text>
                        </g>
                    )
                })}
                <path d={points.map((q, i) => `${i ? 'L' : 'M'}${q[0]} ${q[1]}`).join(' ')} fill="none" stroke="var(--tot)" strokeWidth="2"/>
                {points.map((q, p) => (
                    <circle key={`pt-${p}`} cx={q[0]} cy={q[1]} r="3.4" fill="var(--panel)" stroke="var(--tot)" strokeWidth="2" style={{cursor: 'pointer'}} {...tip(p)}/>
                ))}
                <line x1={L} x2={L + iw} y1={T + ih} y2={T + ih} stroke="var(--line-2)"/>
            </svg>
        </div>
    )
}

export default Pareto
